import { apiClientV2, prefetchApiCsrfToken } from "../common";
import type { ProfitPushState, ProfitPushStatus } from "../types";

const JSON_API_MEDIA = "application/vnd.api+json";

/** Error del datamart (o de la validación local, que tiene la misma forma). */
export class ProfitExportError extends Error {
  /** Campo señalado, p. ej. `details.warehouse_code`. Vacío si no vino. */
  readonly errorField: string;

  constructor(message: string, errorField = "") {
    super(message);
    this.name = "ProfitExportError";
    this.errorField = errorField;
  }
}

function parsePushResponse(payload: unknown): ProfitPushStatus {
  const row =
    (payload as { data?: Record<string, unknown> })?.data ??
    (payload as Record<string, unknown>);
  const attrs = (row?.attributes as Record<string, unknown> | undefined) ?? {};
  const read = (snake: string, kebab: string): unknown =>
    attrs[snake] ?? attrs[kebab];
  const num = (value: unknown): number | null => {
    if (value == null || value === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };
  const text = (value: unknown): string => (value == null ? "" : String(value));
  const date = (value: unknown): string | null =>
    value == null || value === "" ? null : String(value);

  return {
    id: text(row?.id),
    order_id: text(read("order_id", "order-id")),
    external_identifier: text(
      read("external_identifier", "external-identifier"),
    ),
    // El estado viene resuelto del backend. Derivarlo acá desde
    // `integration_status` + `integration_error` duplicaría la regla y las dos
    // copias se separarían en el primer cambio.
    state: (text(attrs.state) || "draft") as ProfitPushState,
    editable: Boolean(attrs.editable),
    integration_status: text(read("integration_status", "integration-status")),
    staging_id: num(read("staging_id", "staging-id")),
    profit_order_number: text(
      read("profit_order_number", "profit-order-number"),
    ),
    message: text(attrs.message),
    error_field: text(read("error_field", "error-field")),
    integration_error: text(read("integration_error", "integration-error")),
    rows_created: num(read("rows_created", "rows-created")),
    attempt_count: num(read("attempt_count", "attempt-count")) ?? 0,
    sent_at: date(read("sent_at", "sent-at")),
    processed_at: date(read("processed_at", "processed-at")),
    last_polled_at: date(read("last_polled_at", "last-polled-at")),
  };
}

/**
 * Un rechazo (422) no es un fallo de la aplicación: es el ERP diciendo qué
 * corregir. El mensaje se propaga tal cual — "No existen almacenes con código:
 * ZPR1." es accionable y "Ocurrió un error" no lo es.
 */
function toExportError(err: unknown): ProfitExportError {
  const response = (err as { response?: { status?: number; data?: any } })
    ?.response;
  const data = response?.data ?? {};
  // El renderer JSON:API envuelve el cuerpo del 422 en `errors`, así que el
  // rechazo llega como `{errors: {message, error_field}}` y no como el
  // `{message, error_field}` que devuelve la vista. Se leen las dos formas —y
  // también el array `[{detail}]` de JSON:API— porque si ninguna coincide, el
  // mensaje accionable del datamart se pierde detrás de un texto genérico, que
  // es justo lo que este flujo no puede permitirse.
  const wrapped = data.errors;
  const fromArray = Array.isArray(wrapped) ? wrapped[0] : undefined;
  const fromObject =
    wrapped && !Array.isArray(wrapped) && typeof wrapped === "object"
      ? wrapped
      : undefined;
  const message =
    fromObject?.message ??
    data.message ??
    fromArray?.detail ??
    data.detail ??
    "No se pudo cargar la orden en Profit.";
  const errorField =
    fromObject?.error_field ??
    fromObject?.["error-field"] ??
    data.error_field ??
    data["error-field"] ??
    "";
  return new ProfitExportError(String(message), String(errorField));
}

const basePath = (simulationId: string | number, orderId: string | number) =>
  `simulations/${simulationId}/simulated-purchase-orders/${orderId}/profit-export/`;

/** POST — crea la OC en Profit. No tiene vuelta atrás desde la aplicación. */
export async function apiPushOrderToProfit(
  simulationId: string | number,
  orderId: string | number,
): Promise<ProfitPushStatus> {
  await prefetchApiCsrfToken();
  try {
    const res = await apiClientV2.post(
      basePath(simulationId, orderId),
      {
        data: {
          type: "profit-pushes",
          id: String(orderId),
          attributes: {},
        },
      },
      {
        headers: {
          Accept: JSON_API_MEDIA,
          "Content-Type": JSON_API_MEDIA,
        },
      },
    );
    return parsePushResponse(res.data);
  } catch (err) {
    throw toExportError(err);
  }
}

/** GET — estado del envío. `draft` si la orden nunca se envió. */
export async function apiGetProfitPushStatus(
  simulationId: string | number,
  orderId: string | number,
  options?: { refresh?: boolean },
): Promise<ProfitPushStatus> {
  const res = await apiClientV2.get(basePath(simulationId, orderId), {
    headers: { Accept: JSON_API_MEDIA },
    params: options?.refresh ? { refresh: true } : {},
  });
  return parsePushResponse(res.data);
}

/**
 * GET — estado de todas las órdenes de la simulación, en una llamada.
 *
 * Con `refresh` el backend consulta el datamart por `externalIdentifier` antes
 * de responder. Se usa al abrir la pantalla y no en el polling: es lo que
 * vuelve el estado recuperable aunque se haya perdido el registro local, pero
 * cuesta una llamada al datamart por orden abierta.
 */
export async function apiListProfitPushes(
  simulationId: string | number,
  options?: { refresh?: boolean },
): Promise<ProfitPushStatus[]> {
  const res = await apiClientV2.get(`simulations/${simulationId}/profit-pushes/`, {
    headers: { Accept: JSON_API_MEDIA },
    params: options?.refresh ? { refresh: true } : {},
  });
  const rows = (res.data as { data?: unknown[] })?.data ?? res.data ?? [];
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => parsePushResponse({ data: row }));
}
